export type RefreshKind = "calendar" | "conferencing";
export type RefreshQueryKey = "calendar-status" | "conferencing-status";

/**
 * Which status queries must be refetched after an integration changes.
 *
 * <p>The two payloads are not independent, which is easy to get wrong because the names suggest
 * they are:
 *
 * <ul>
 *   <li><b>calendar-status</b> carries the calendar connections <em>and</em> the default meeting
 *       link ({@code conferencingRuntime.defaultProvider}).</li>
 *   <li><b>conferencing-status</b> carries per-provider connection state.</li>
 * </ul>
 *
 * <p>So a conferencing change can move a value that only calendar-status reports: the backend
 * stands a default down once its provider can no longer serve, and disconnecting Zoom while Zoom
 * is the default drops it to "no default link". Refreshing only conferencing-status left the
 * conferencing card showing a provider the server had already discarded until a full reload.
 *
 * <p>The reverse coupling was already handled: a calendar change refreshes conferencing-status
 * when the provider is calendar-backed, since Meet and Teams are minted by the calendar account.
 */
export function queriesToRefresh(
  kind?: RefreshKind,
  isCalendarBackedConferencing = false,
): RefreshQueryKey[] {
  if (kind === "calendar") {
    return isCalendarBackedConferencing
      ? ["calendar-status", "conferencing-status"]
      : ["calendar-status"];
  }
  if (kind === "conferencing") {
    // Always both: the default meeting link lives on calendar-status.
    return ["conferencing-status", "calendar-status"];
  }
  return ["calendar-status", "conferencing-status"];
}
